import React, { useMemo } from 'react';

import { useBackdrop } from '@/context/BackdropContext';
import { cn } from '@/lib/utils';

interface BackdropOverlayProps {
  className?: string;
  onClick?: () => void;
  zIndex?: number;
}

/**
 * Backdrop Overlay Component - Optimized for low-resource PCs
 * Automatically shows/hides based on global backdrop context state
 * Lightweight version without blur effects or animations for better performance
 */
export const BackdropOverlay: React.FC<BackdropOverlayProps> = ({
  className,
  onClick,
  zIndex = 25, // Between sidebar (z-30) and content, but above main content
}) => {
  const { isBackdropActive, hideBackdrop, activeMenuId } = useBackdrop();

  // Memoized backdrop classes - optimized for low-resource PCs
  const backdropClass = useMemo(
    () => cn('fixed inset-0 bg-black/10', 'dark:bg-black/20', className),
    [className],
  );

  // Handle backdrop click - close all menus
  const handleBackdropClick = () => {
    if (onClick) {
      onClick();
    } else if (activeMenuId) {
      hideBackdrop(activeMenuId);
    }
  };

  // No animations for low-resource PC optimization
  return isBackdropActive ? (
    <div
      className={backdropClass}
      style={{ zIndex }}
      onClick={handleBackdropClick}
      aria-hidden="true"
      role="presentation"
    />
  ) : null;
};

// Enhanced backdrop with stronger blur for important modals
export const StrongBackdropOverlay: React.FC<BackdropOverlayProps> = (
  props,
) => {
  const backdropClass = useMemo(
    () =>
      cn(
        'fixed inset-0 bg-slate-900/30 backdrop-blur-md',
        'dark:bg-slate-950/50',
        'transition-all duration-300 ease-out',
        props.className,
      ),
    [props.className],
  );

  return <BackdropOverlay {...props} className={backdropClass} />;
};

// Light backdrop for subtle menu overlays
export const LightBackdropOverlay: React.FC<BackdropOverlayProps> = (props) => {
  const backdropClass = useMemo(
    () =>
      cn(
        'fixed inset-0 bg-slate-900/10 backdrop-blur-[2px]',
        'dark:bg-slate-950/20',
        'transition-all duration-200 ease-out',
        props.className,
      ),
    [props.className],
  );

  return <BackdropOverlay {...props} className={backdropClass} />;
};

// Ultra-lightweight backdrop for low-resource PCs - no blur, no animations
export const LowResourceBackdropOverlay: React.FC<BackdropOverlayProps> = ({
  className,
  onClick,
  zIndex = 25,
}) => {
  const { isBackdropActive, hideBackdrop, activeMenuId } = useBackdrop();

  // Handle backdrop click - close all menus
  const handleBackdropClick = () => {
    if (onClick) {
      onClick();
    } else if (activeMenuId) {
      hideBackdrop(activeMenuId);
    }
  };

  // Simple backdrop without blur or animations for performance
  const backdropClass = useMemo(
    () => cn('fixed inset-0 bg-black/10', 'dark:bg-black/20', className),
    [className],
  );

  // No animations, just show/hide for maximum performance
  return isBackdropActive ? (
    <div
      className={backdropClass}
      style={{ zIndex }}
      onClick={handleBackdropClick}
      aria-hidden="true"
      role="presentation"
    />
  ) : null;
};
